import type {
    BillingCheckoutRequest,
    BillingStateResponse,
    BillingSwitchRequest,
    CancelResponse,
    CheckoutResponse,
    PlansResponse,
    PromoRedeemRequest,
    PromoRedeemResponse,
    ResumeResponse,
    SubscriptionInvoicesResponse,
    SwitchResponse,
    UpdateBillingCompanyRequest,
    UpdateBillingCompanyResponse,
} from '@/types';
import type { AxiosInstance, AxiosResponse } from 'axios';

/**
 * Billing / subscription management (MILO-181 / MILO-97).
 *
 * Wraps the /api/billing endpoints. checkout/cancel/updateCompany are
 * owner-only on the backend (403 for members) — gate the UI on `is_owner`
 * from the me-payload before calling them.
 */
class BillingService {
    private readonly apiService;

    constructor(apiService: AxiosInstance) {
        this.apiService = apiService;
    }

    /** Current subscription state of the account (the literal subscription row). */
    show(): Promise<AxiosResponse<BillingStateResponse>> {
        return this.apiService.get('/api/billing');
    }

    /** Available plans with prices, limits, features and add-ons (plan picker). */
    plans(): Promise<AxiosResponse<PlansResponse>> {
        return this.apiService.get('/api/billing/plans');
    }

    /**
     * Start a provider checkout for the chosen plan. Returns the hosted
     * checkout URL the customer must be redirected to.
     */
    checkout(request: BillingCheckoutRequest): Promise<AxiosResponse<CheckoutResponse>> {
        return this.apiService.post('/api/billing/checkout', request);
    }

    /**
     * Redeem a promo code for the account (MILO-66). Owner-only; a refused code
     * comes back as a 422 with `errors.code`, deliberately indistinguishable
     * between unknown, expired and exhausted. With a running subscription the
     * provider amount is lowered at once, so the very next debit is discounted.
     * The endpoint's 429 (6 attempts a minute) gets its own message under the
     * field (useBilling), so the global rate-limit notice stays out of it.
     */
    redeemPromo(request: PromoRedeemRequest): Promise<AxiosResponse<PromoRedeemResponse>> {
        return this.apiService.post('/api/billing/promo', request, { skipRateLimitNotice: true });
    }

    /**
     * Switch the running subscription to another plan/interval. Upgrades apply
     * immediately; downgrades and interval changes are scheduled for the period
     * end (reflected in the `next_*` fields). Owner-only; 422 on an over-limit fit.
     */
    switch(request: BillingSwitchRequest): Promise<AxiosResponse<SwitchResponse>> {
        return this.apiService.post('/api/billing/switch', request);
    }

    /** Cancel the running subscription; access continues until the period end. */
    cancel(): Promise<AxiosResponse<CancelResponse>> {
        return this.apiService.post('/api/billing/cancel');
    }

    /**
     * Undo a cancellation while the paid period still runs: billing resumes at
     * the period end on the existing mandate (no checkout, no charge now).
     * Owner-only; 422 when there is nothing to resume.
     */
    resume(): Promise<AxiosResponse<ResumeResponse>> {
        return this.apiService.post('/api/billing/resume');
    }

    /** Designate which company represents the account's billing identity. */
    updateCompany(request: UpdateBillingCompanyRequest): Promise<AxiosResponse<UpdateBillingCompanyResponse>> {
        return this.apiService.put('/api/billing/company', request);
    }

    /**
     * Milo's own VAT invoices for this account's subscription fees (MILO-184),
     * newest first. Readable by anyone on the account, not just the owner — the
     * customer also gets each one by mail, so this is the place to find it back.
     */
    invoices(): Promise<AxiosResponse<SubscriptionInvoicesResponse>> {
        return this.apiService.get('/api/billing/invoices');
    }

    /** The stored PDF of one subscription invoice. */
    downloadInvoice(id: string): Promise<AxiosResponse<Blob>> {
        return this.apiService.get(`/api/billing/invoices/${id}/pdf`, { responseType: 'blob' });
    }
}

export default BillingService;
